/**
 * Builds a full name string from patient name parts, normalizing whitespace so that
 * searching "test pa" still matches "test patient10" (FirstName="test", LastName="patient10").
 *
 * The key fix: join non-empty name parts with a SINGLE space so there are no double-spaces
 * even when MiddleName is empty/null.
 */
export const buildPatientFullName = (patient) => {
  return [patient?.FirstName, patient?.MiddleName, patient?.LastName]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .trim()
}

/**
 * Checks whether a patient matches a given free-text search query.
 * Matches against: full name (first + middle + last), first name only, last name only,
 * facility name, facility parent company, room number, patient ID, phone number, DOB.
 *
 * Also supports multi-word queries: each space-separated word must appear somewhere in
 * the candidate text, allowing "test pa" to match "test patient10".
 *
 * @param {object} patient - The patient object
 * @param {string} query   - The raw search string typed by the user
 * @param {object} [extra] - Optional extra fields: { facilityName, facilityParentCompany, providerName }
 * @returns {boolean}
 */
export const patientMatchesSearch = (patient, query, extra = {}) => {
  if (!query || !query.trim()) return true

  const q = query.toLowerCase().trim()

  const fullName = buildPatientFullName(patient)
  const firstName = (patient?.FirstName || '').toLowerCase()
  const middleName = (patient?.MiddleName || '').toLowerCase()
  const lastName = (patient?.LastName || '').toLowerCase()
  const facilityName = (extra.facilityName ?? patient?.FacilityName ?? '').toString().toLowerCase()
  const facilityParentCompany = (extra.facilityParentCompany ?? patient?.facilityParentCompany ?? '').toString().toLowerCase()
  const providerName = (extra.providerName ?? patient?.ProviderName ?? '').toString().toLowerCase()
  const roomNumber = (patient?.Room?.toString() ?? '').toLowerCase()
  const patientId = (patient?.PatientID?.toString() ?? '').toLowerCase()
  const phoneNumber = (patient?.PhoneNumber ?? '').toString().toLowerCase()
  const dob = (patient?.DOB ?? '').toString().toLowerCase()

  const candidates = [fullName, firstName, middleName, lastName, facilityName, facilityParentCompany, providerName, roomNumber, patientId, phoneNumber, dob]

  // Split query into words — every word must be found in at least one candidate field
  const words = q.split(/\s+/).filter(Boolean)
  return words.every((word) => candidates.some((c) => c.includes(word)))
}

/**
 * A generic name-match helper for simple name-only searches
 * (e.g. provider or facility name fields that are just one string).
 */
export const nameMatchesSearch = (name, query) => {
  if (!query || !query.trim()) return true
  const q = query.toLowerCase().trim()
  const n = (name || '').toLowerCase()
  const words = q.split(/\s+/).filter(Boolean)
  return words.every((word) => n.includes(word))
}
